import ErrorIcon from '@mui/icons-material/ErrorOutline'
import { PlayIcon } from 'components/Icons'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { isVideoUrl } from 'utils/composer'

import styles from './NativeVideoPlayer.pcss'
const DEFAULT_VIDEO_CONTROLS_LIST = 'nodownload nofullscreen noremoteplayback'

export interface NativeVideoPlayerProps {
  videoUrl?: string
  videoDataUrl?: string
  thumbnailUrl?: string
  togglePlayOnHover?: boolean
  controls?: boolean
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  controlsList?: string
  backgroundImage?: string
  className?: string
  mediaElementId?: string
  videoElementClassName?: string
  playBtnClassName?: string
  retry?: {
    max: number
    timeout: number // ms
  }
  // Defaults to 'none' when a thumbnail is shown as the poster (nothing is downloaded until play), 'metadata' otherwise.
  // Note that with 'none', onCanPlay / onError (and retries) only fire once playback starts.
  preload?: 'auto' | 'metadata' | 'none'
  onCanPlay?: () => void
  onError?: () => void
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
}

export function NativeVideoPlayer(props: NativeVideoPlayerProps) {
  const isValidUrl = Boolean(props.videoDataUrl) || Boolean(props.videoUrl && isVideoUrl(props.videoUrl))
  const [playing, setPlaying] = React.useState(Boolean(props.autoplay))
  const [loadFailed, setLoadFailed] = React.useState(false)
  // Number of retries used for the current source. Counts against props.retry.max.
  const [retryCount, setRetryCount] = React.useState(0)
  // Bumped to remount the <video> when the source or thumbnail changes:
  // changing <source src> alone does not reload a video, and Chrome keeps showing the loaded frame instead of a new poster.
  const [mountVersion, setMountVersion] = React.useState(0)
  const src = props.videoUrl || props.videoDataUrl
  const [prevSrc, setPrevSrc] = React.useState(src)
  const [prevThumbnailUrl, setPrevThumbnailUrl] = React.useState(props.thumbnailUrl)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const retryTimerRef = React.useRef<number>(undefined)

  // Reset during render (not in an effect) so a stale <video> or error state never commits
  if (prevSrc !== src || prevThumbnailUrl !== props.thumbnailUrl) {
    if (prevSrc !== src) {
      setRetryCount(0)
    }
    setPrevSrc(src)
    setPrevThumbnailUrl(props.thumbnailUrl)
    setMountVersion(current => current + 1)
    setLoadFailed(false)
    setPlaying(Boolean(props.autoplay))
  }

  // Drop a pending retry when the source changes or the player unmounts
  React.useEffect(() => () => window.clearTimeout(retryTimerRef.current), [src])

  const error = !isValidUrl || loadFailed
  const preload = props.preload || (props.thumbnailUrl && !props.autoplay ? 'none' : 'metadata')
  const backgroundStyle = props.backgroundImage ? { backgroundImage: `url(${props.backgroundImage})` } : {}

  const startPlayback = (player: HTMLVideoElement) => {
    setPlaying(true)
    // play() rejects when interrupted by pause() (quick hover out) or blocked by autoplay policy
    player.play()?.catch(() => setPlaying(!player.paused))
  }

  const onPlayIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    playVideo()
  }

  const playVideo = () => {
    if (props.onClick) {
      return
    }
    const player = videoRef.current
    if (player) {
      startPlayback(player)
    }
  }

  const hoverPlay = () => {
    if (props.togglePlayOnHover) {
      playVideo()
    }
  }

  const hoverPause = () => {
    if (props.togglePlayOnHover) {
      pauseVideo()
    }
  }

  const pauseVideo = () => {
    const player = videoRef.current
    if (player) {
      player.pause()
      setPlaying(false)
    }
  }

  const togglePlay = () => {
    const player = videoRef.current
    if (!player) {
      return
    }

    if (player.paused) {
      startPlayback(player)
    } else {
      player.pause()
      setPlaying(false)
    }
  }

  const onVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    console.log('[NativeVideoPlayer] error loading video', e)
    if (props.retry && retryCount < props.retry.max) {
      window.clearTimeout(retryTimerRef.current)
      retryTimerRef.current = window.setTimeout(() => {
        console.log('retrying...')
        setRetryCount(current => current + 1)
        setPlaying(Boolean(props.autoplay))
      }, props.retry.timeout)
    } else {
      setLoadFailed(true)
      if (props.onError) {
        props.onError()
      }
    }
  }

  const onCanPlay = () => {
    setLoadFailed(false)
    if (props.onCanPlay) {
      props.onCanPlay()
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (props.onClick) {
      props.onClick(e)
    } else if (!props.controls) {
      // With native controls the browser already toggles playback; toggling here would undo it
      togglePlay()
    }
  }

  return (
    <div className={`${styles['player-box']} ${props.className || ''}`} data-testid="native-video-player" onClick={handleClick}>
      {
        error && (
          <div className={`${styles.error} ${styles.center}`}>
            <ErrorIcon className={styles.icon} />
            <p><FormattedMessage id="errors.video.loading" /></p>
          </div>
        )
      }
      {
        props.backgroundImage && (
          <div className={`${styles.background}`} style={backgroundStyle}></div>
        )
      }
      <div className={styles.media}>
        {
          !playing && (
            <span className={styles['btn-play']} onClick={onPlayIconClick}>
              <PlayIcon className={props.playBtnClassName || ''} />
            </span>
          )
        }
        <video
          poster={props.thumbnailUrl}
          ref={videoRef}
          key={`${mountVersion}-${retryCount}`}
          preload={preload}
          autoPlay={props.autoplay}
          controls={props.controls}
          controlsList={props.controlsList || DEFAULT_VIDEO_CONTROLS_LIST}
          loop={props.loop}
          muted={props.muted}
          data-id={props.mediaElementId}
          data-testid={props.mediaElementId}
          className={`${styles.video} ${props.videoElementClassName || ''}`}
          onMouseEnter={hoverPlay}
          onMouseLeave={hoverPause}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={onVideoError}
          onCanPlay={onCanPlay}
        >
          {isValidUrl && <source src={src} />}
          <p><FormattedMessage id="errors.video.not-supported" /></p>
        </video>
      </div>
    </div>
  )
}

export default NativeVideoPlayer
